/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  Grid,
  Box,
} from '@mui/material';
import { Add, ArrowBack, Settings as SettingsIcon } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DataTable from 'examples/Tables/DataTable';
import { useTheme } from '@mui/material/styles';
import PageHeader from 'components/v2/PageHeader';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import CustomObjectFieldRenderer from 'components/CustomObjectFields/CustomObjectFieldRenderer';
import { formatCellValue, validateRecordValues } from 'components/SettingsComponents/customObjectsFieldTypes';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import { uploadPrivateObjectsBulk, uploadPublicObjectsBulk } from 'utils/S3';
import {
  inferImageIsPublic,
  resolveImageFeaturePath,
} from 'utils/customObjectImagePrivacy';

const LIST_COLUMN_COUNT = 5;

/** Lower score = shown first in the list table. */
function listFieldScore(field) {
  const name = (field.name || '').toLowerCase();
  const type = field.type || '';
  if (name === 'external_id' || name.endsWith('_external_id')) return 100;
  if (type === 'url') return 90;
  if (type === 'long_text' && !/(name|title|header|label|code|description)/.test(name)) return 80;
  if (/(name|title|header_text|label|code|status|type)/.test(name)) return 0;
  if (type === 'image') return 15;
  if (type === 'relation' || type === 'dropdown' || type === 'boolean' || type === 'checkbox') return 10;
  if (type === 'number' || type === 'decimal' || type === 'currency') return 20;
  return 40;
}

function TruncatedCell({ text, maxWidth = 180 }) {
  // PSA-373: wrap long values instead of truncating with "...".
  const display = text == null || text === '' ? '—' : String(text);
  return (
    <MDBox
      component="span"
      display="block"
      title={display !== '—' ? display : undefined}
      sx={{
        maxWidth,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.35,
      }}
    >
      {display}
    </MDBox>
  );
}

export default function CustomObjectList() {
  const { apiName } = useParams();
  // Custom object pages had no permission check at all — support users could
  // create and edit records here even though the identical action is blocked on
  // every standard object. `co_<apiName>` is the registry key the backend uses
  // for tenant-defined objects.
  const { canCreate } = usePermission(`co_${apiName}`);
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  const [definition, setDefinition] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // UI v2 list layout: the same title, description and actions in the page header.
  const isV2 = useTheme()?.pearlUi === 'v2';
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [islandRecords, setIslandRecords] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isComicPages = apiName === 'comic_page';

  const editableFields = useMemo(() => {
    if (!definition) return [];
    return (definition.sections || []).flatMap((s) =>
      (s.fields || []).filter((f) => !f.readOnly && !['created_by', 'created_at', 'updated_by', 'updated_at'].includes(f.name))
    );
  }, [definition]);

  const listFields = useMemo(() => {
    if (!editableFields.length) return [];
    return [...editableFields]
      .sort((a, b) => listFieldScore(a) - listFieldScore(b))
      .slice(0, LIST_COLUMN_COUNT);
  }, [editableFields]);

  const createFields = useMemo(() => {
    if (!isComicPages) return editableFields;
    // Sequence is derived from the uploaded/rearranged image order — hide the manual input.
    const order = {
      island: 0,
      title: 1,
      description: 2,
      page_image: 3,
      status: 4,
      external_id: 5,
    };
    return [...editableFields]
      .filter((field) => field.name !== 'sequence')
      .sort((a, b) => (order[a.name] ?? 50) - (order[b.name] ?? 50));
  }, [editableFields, isComicPages]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        axiosInstance.get(`/custom-objects/api/${apiName}`),
        axiosInstance.get(`/custom-objects/${apiName}/data`, {
          params: {
            limit: 200,
            search: search || undefined,
            ...(apiName === 'comic_page'
              ? { sort_by: 'sequence', sort_order: 'asc' }
              : {}),
          },
        }),
      ];
      if (apiName === 'comic_page') {
        requests.push(
          axiosInstance.get('/custom-objects/island/data', {
            params: { limit: 1000, sort_by: 'island_name', sort_order: 'asc' },
          })
        );
      }
      const [defRes, dataRes, islandsRes] = await Promise.all(requests);
      setDefinition(defRes.data);
      setRecords(dataRes.data || []);
      setIslandRecords(islandsRes?.data || []);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load records', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, apiName, search]);

  const islandLabels = useMemo(
    () =>
      new Map(
        islandRecords.map((island) => [
          island.id,
          `${island.island_name || island.name || 'Island'} (#${island.id})`,
        ])
      ),
    [islandRecords]
  );

  const tableRecords = useMemo(() => {
    if (!isComicPages) return records;
    const groups = new Map();
    [...records]
      .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0))
      .forEach((record) => {
        const key = record.island ?? `unassigned-${record.id}`;
        const current = groups.get(key);
        if (!current) {
          groups.set(key, {
            ...record,
            _pageCount: 1,
            _islandLabel:
              islandLabels.get(record.island) ||
              (record.island ? `Island #${record.island}` : 'Unassigned'),
          });
        } else {
          current._pageCount += 1;
        }
      });
    return Array.from(groups.values());
  }, [isComicPages, records, islandLabels]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo(() => {
    if (isComicPages) {
      return [
        {
          Header: 'Cover',
          accessor: 'page_image',
          width: '96px',
          disableSortBy: true,
          Cell: ({ value }) => {
            const src = typeof value === 'string' ? value : value?.url;
            return src ? (
              <Box
                component="img"
                src={src}
                alt={(typeof value === 'object' && value?.file_name) || 'Comic cover'}
                sx={{
                  width: 72,
                  height: 72,
                  objectFit: 'cover',
                  borderRadius: 1.5,
                  display: 'block',
                }}
              />
            ) : '—';
          },
        },
        {
          Header: 'Comic',
          accessor: 'title',
          width: '220px',
          Cell: ({ value }) => <TruncatedCell text={formatCellValue({ type: 'long_text' }, value)} maxWidth={220} />,
        },
        {
          Header: 'Island',
          accessor: '_islandLabel',
          width: '200px',
          Cell: ({ value }) => <TruncatedCell text={value} maxWidth={200} />,
        },
        {
          Header: 'Pages',
          accessor: '_pageCount',
          width: '80px',
          align: 'center',
        },
        {
          Header: 'Status',
          accessor: 'status',
          width: '110px',
        },
        {
          Header: 'Actions',
          accessor: 'actions',
          width: '110px',
          align: 'center',
          disableSortBy: true,
          Cell: ({ row }) => (
            <MDButton
              size="small"
              color="info"
              onClick={() => navigate(`/custom-objects/${apiName}/${row.original.id}`)}
            >
              View
            </MDButton>
          ),
        },
      ];
    }
    const cols = listFields.map((field) => ({
      Header: field.label,
      accessor: field.name,
      width: field.type === 'url' || field.type === 'long_text' ? '180px' : '140px',
      Cell: ({ value }) => (
        field.type === 'image' && (typeof value === 'string' ? value : value?.url) ? (
          <Box
            component="img"
            src={typeof value === 'string' ? value : value.url}
            alt={(typeof value === 'object' && value?.file_name) || field.label}
            sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
          />
        ) : (
          <TruncatedCell
            text={formatCellValue(field, value)}
            maxWidth={field.type === 'url' || field.type === 'long_text' ? 200 : 160}
          />
        )
      ),
    }));
    cols.push({
      Header: 'Actions',
      accessor: 'actions',
      width: '110px',
      align: 'center',
      disableSortBy: true,
      Cell: ({ row }) => (
        <MDButton
          size="small"
          color="info"
          onClick={() => navigate(`/custom-objects/${apiName}/${row.original.id}`)}
        >
          View
        </MDButton>
      ),
    });
    return cols;
  }, [listFields, navigate, apiName, isComicPages]);

  const openCreate = () => {
    const existingImages = Array.isArray(formData.page_image)
      ? formData.page_image
      : [];
    existingImages.forEach((image) => {
      if (image?.is_local_preview && image.url) {
        URL.revokeObjectURL(image.url);
      }
    });
    const initial = {};
    editableFields.forEach((f) => {
      if (f.type === 'boolean' || f.type === 'checkbox') initial[f.name] = false;
      else if (f.type === 'select_list' || f.type === 'multirelation') initial[f.name] = [];
    });
    if (isComicPages) {
      initial.page_image = [];
      initial.status = 'ACTIVE';
    }
    setFormData(initial);
    setFieldErrors({});
    setFormError('');
    setModalOpen(true);
  };

  const closeCreate = () => {
    const stagedImages = Array.isArray(formData.page_image)
      ? formData.page_image
      : [];
    stagedImages.forEach((image) => {
      if (image?.is_local_preview && image.url) {
        URL.revokeObjectURL(image.url);
      }
    });
    setModalOpen(false);
    setFormData({});
    setFieldErrors({});
    setFormError('');
  };

  const saveRecord = async () => {
    const fieldsToValidate = isComicPages ? createFields : editableFields;
    const { isValid, fieldErrors: nextErrors, messages } = validateRecordValues(
      fieldsToValidate,
      formData
    );
    if (!isValid) {
      setFieldErrors(nextErrors);
      setFormError(messages.join(' · '));
      return;
    }
    setFieldErrors({});
    setFormError('');
    setSaving(true);
    try {
      const images =
        isComicPages && Array.isArray(formData.page_image)
          ? formData.page_image
          : null;
      if (images?.length) {
        const stagedFiles = images.map((image) => image.file).filter(Boolean);
        if (stagedFiles.length !== images.length) {
          throw new Error(
            'One or more comic pages are no longer available. Please select them again.'
          );
        }
        const usePublic = inferImageIsPublic(images);
        const uploadFn = usePublic ? uploadPublicObjectsBulk : uploadPrivateObjectsBulk;
        const uploaded = await uploadFn({
          axiosInstance,
          files: stagedFiles,
          featurePath: resolveImageFeaturePath(apiName),
          scope: 'org',
          includeDatePartition: true,
        });
        if (uploaded.length !== images.length) {
          throw new Error('The bulk image upload returned an incomplete result.');
        }
        const uploadedImages = uploaded.map((image) => image.url).filter(Boolean);
        if (uploadedImages.length !== images.length) {
          throw new Error('The bulk image upload returned incomplete image URLs.');
        }
        const createdIds = [];
        try {
          for (let index = 0; index < uploadedImages.length; index += 1) {
            const payload = {
              ...formData,
              page_image: uploadedImages[index],
              // Upload / rearrange order is the page sequence (1-based).
              sequence: index + 1,
            };
            if (formData.external_id && uploadedImages.length > 1) {
              payload.external_id = `${formData.external_id}-${index + 1}`;
            }
            const response = await axiosInstance.post(
              `/custom-objects/${apiName}/data`,
              payload
            );
            createdIds.push(response.data.id);
          }
        } catch (batchError) {
          await Promise.allSettled(
            createdIds.map((recordId) =>
              axiosInstance.delete(`/custom-objects/${apiName}/data/${recordId}`)
            )
          );
          throw batchError;
        }
      } else {
        await axiosInstance.post(`/custom-objects/${apiName}/data`, formData);
      }
      closeCreate();
      setSnackbar({
        open: true,
        message: images?.length
          ? `${images.length} comic page${images.length === 1 ? '' : 's'} created`
          : 'Record created',
        severity: 'success',
      });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(', ')
        : (detail || error.message || 'Failed to save record');
      setFormError(message);
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 0 : 6} pb={3}>
        {isV2 ? (
          <PageHeader
            title={definition?.pluralName || apiName}
            summary={definition?.description}
            actions={
              <>
                <TextField size="small" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchData()} />
                {canCreate && (
                  <MDButton color="info" onClick={openCreate}><Add sx={{ mr: 0.5 }} /> Add</MDButton>
                )}
                <MDButton variant="outlined" color="dark" onClick={() => navigate('/settings/custom-objects')} aria-label="Custom object settings"><SettingsIcon /></MDButton>
              </>
            }
          />
        ) : (
        <Card sx={{ p: 3, mb: 3 }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <MDBox display="flex" alignItems="center" gap={1} minWidth={0} flex={1}>
              <IconButton onClick={() => navigate('/custom-objects')}><ArrowBack /></IconButton>
              <MDBox minWidth={0}>
                <MDTypography variant="h4">{definition?.pluralName || apiName}</MDTypography>
                <MDTypography
                  variant="body2"
                  color="text"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {definition?.description}
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDBox display="flex" gap={1} flexShrink={0} flexWrap="wrap">
              <TextField size="small" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchData()} />
              {canCreate && (
                <MDButton color="info" onClick={openCreate}><Add sx={{ mr: 0.5 }} /> Add</MDButton>
              )}
              <MDButton color="secondary" onClick={() => navigate('/settings/custom-objects')}><SettingsIcon /></MDButton>
            </MDBox>
          </MDBox>
        </Card>
        )}

        {loading ? (
          <DataLoadingSpinner
            entityType="default"
            title="Loading Custom Object"
            message="Please wait while we fetch the custom object records..."
          />
        ) : (
          <Card
            sx={{
              overflow: 'hidden',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <MDBox
              sx={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                '& table': {
                  minWidth: '100%',
                },
                '& thead th:last-of-type, & tbody td:last-of-type': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 3,
                  backgroundColor: '#fff',
                  boxShadow: '-6px 0 10px -6px rgba(15, 23, 42, 0.18)',
                },
                '& thead th:last-of-type': {
                  zIndex: 4,
                },
              }}
            >
              <DataTable
                table={{ columns, rows: tableRecords }}
                canSearch={false}
                showTotalEntries
                showFilters={false}
                entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
              />
            </MDBox>
          </Card>
        )}

        <Dialog open={modalOpen} onClose={closeCreate} maxWidth="md" fullWidth>
          <DialogTitle>
            {isComicPages ? 'Create Comic Pages' : `Create ${definition?.name || 'Record'}`}
          </DialogTitle>
          <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            {isComicPages && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Select an Island, then upload all comic pages together. Numbered
                badges show page order; rearrange with the arrows before saving.
                The first image is the cover.
              </Alert>
            )}
            <Grid container spacing={2}>
              {createFields.map((field) => {
                const fullWidth =
                  field.type === 'long_text' ||
                  field.type === 'image' ||
                  field.type === 'relation';
                return (
                <Grid item xs={12} md={fullWidth ? 12 : 6} key={field.name}>
                  <CustomObjectFieldRenderer
                    field={field}
                    value={formData[field.name]}
                    error={fieldErrors[field.name]}
                    onChange={(val) => {
                      setFormData((prev) => ({ ...prev, [field.name]: val }));
                      if (fieldErrors[field.name]) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next[field.name];
                          return next;
                        });
                      }
                    }}
                    axiosInstance={axiosInstance}
                    multipleImages={isComicPages && field.name === 'page_image'}
                    featurePath={resolveImageFeaturePath(apiName)}
                  />
                </Grid>
                );
              })}
            </Grid>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={closeCreate}>Cancel</MDButton>
            <MDButton color="info" onClick={saveRecord} disabled={saving}>
              {saving
                ? 'Saving...'
                : isComicPages && Array.isArray(formData.page_image) && formData.page_image.length
                  ? `Create ${formData.page_image.length} Page${formData.page_image.length === 1 ? '' : 's'}`
                  : 'Save'}
            </MDButton>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}
